import { CalendarDate, isEqualDay, isEqualMonth, isEqualYear } from "@internationalized/date";
import { InspectionRequestPreferenceMeridiem } from "./InspectionRequestPreferenceMeridiem";

interface InspectionTypeScheduleCalendarDayCountArgs {
  /**
   * The date the inspection is scheduled at. Possibly null if the inspection has not been scheduled
   */
  inspectionScheduledAtDate: CalendarDate | null;
  inspectionScheduledMeridiem: InspectionRequestPreferenceMeridiem | null;
  /**
   * The id of the inspector selected on the scheduler
   */
  selectedInspectorId: string | null;
  /**
   * The selected date on the scheduler
   */
  selectedCalendarDate: CalendarDate | null;
  /**
   * The selected meridiem on the scheduler calendar
   */
  selectedCalendarMeridiem: InspectionRequestPreferenceMeridiem | null;
  /**
   * The id of the inspector row
   */
  currentInspectorId: string;
  /**
   * The current count of the date/meridiem
   */
  currentCalendarDateCount: number;
  /**
   * The date on the calendar
   */
  currentCalendarDate: CalendarDate;
  /**
   * The current meridiem
   */
  currentCalendarMeridiem: InspectionRequestPreferenceMeridiem;
  /**
   * The id of the user assigned to the inspection
   */
  inspectionScheduledAssigneeId: string | null;
}

export default class InspectionTypeScheduleCalendarDayCount {
  private inspectionScheduledAtDate: CalendarDate | null;
  private inspectionScheduledAssigneeId: string | null;
  private inspectionScheduledMeridiem: InspectionRequestPreferenceMeridiem | null;
  private selectedCalendarDate: CalendarDate | null;
  private selectedCalendarMeridiem: InspectionRequestPreferenceMeridiem | null;
  private currentCalendarDateCount: number;
  private currentCalendarDate: CalendarDate;
  private currentCalendarMeridiem: InspectionRequestPreferenceMeridiem;
  private selectedInspectorId: string | null;
  private currentInspectorId: string;

  constructor(args: InspectionTypeScheduleCalendarDayCountArgs) {
    this.inspectionScheduledAtDate = args.inspectionScheduledAtDate;
    this.selectedCalendarDate = args.selectedCalendarDate;
    this.selectedCalendarMeridiem = args.selectedCalendarMeridiem;
    this.currentCalendarDateCount = args.currentCalendarDateCount;
    this.currentCalendarDate = args.currentCalendarDate;
    this.currentCalendarMeridiem = args.currentCalendarMeridiem;
    this.selectedInspectorId = args.selectedInspectorId;
    this.currentInspectorId = args.currentInspectorId;
    this.inspectionScheduledAssigneeId = args.inspectionScheduledAssigneeId;
    this.inspectionScheduledMeridiem = args.inspectionScheduledMeridiem;
  }

  /**
   * Gets the number to be displayed in the cell on the scheduler for a given inspector/date/meridiem. Its messy
   * and I hate it. I'm sorry :(
   */
  getCount(): number {
    if (this.inspectionScheduledAtDate === null) {
      if (this.selectedEqualsCurrent()) {
        return this.currentCalendarDateCount + 1;
      } else {
        return this.currentCalendarDateCount;
      }
    }

    if (this.scheduledEqualsCurrent()) {
      if (this.selectedEqualsCurrent()) {
        return this.currentCalendarDateCount;
      }
      return this.currentCalendarDateCount - 1;
    }

    if (this.selectedEqualsCurrent()) {
      return this.currentCalendarDateCount + 1;
    }

    return this.currentCalendarDateCount;
  }

  // Checks if the selected date/meridiem/inspector is the same as the current cell
  private selectedEqualsCurrent(): boolean {
    if (this.selectedCalendarDate === null) {
      return false;
    }
    if (this.currentInspectorId.toString() !== this.selectedInspectorId?.toString()) {
      return false;
    }
    return (
      this.dateYearMonthDayEqual(this.selectedCalendarDate, this.currentCalendarDate) &&
      this.selectedCalendarMeridiem === this.currentCalendarMeridiem
    );
  }

  // Checks if the scheduled date/meridiem/inspector is the same as the current cell
  private scheduledEqualsCurrent(): boolean {
    if (this.inspectionScheduledAtDate === null) {
      return false;
    }
    if (this.currentInspectorId.toString() !== this.inspectionScheduledAssigneeId?.toString()) {
      return false;
    }
    return (
      this.dateYearMonthDayEqual(this.inspectionScheduledAtDate, this.currentCalendarDate) &&
      this.inspectionScheduledMeridiem === this.currentCalendarMeridiem
    );
  }

  private dateYearMonthDayEqual(date: CalendarDate, other: CalendarDate): boolean {
    return isEqualDay(date, other) && isEqualMonth(date, other) && isEqualYear(date, other);
  }
}
